import { useAuth } from "@/features/auth";
import { trackReceivedWebsocketEvent } from "@/shared/utils/datadog-logger";
import { REALTIME_TOPIC_ROLES, RSOCKET_TOPICS, TopicKey } from "../realtime-topics";
import { Event, TopicMessageHandler } from "../types";
import { useRSocketTopic } from "./useRSocketTopic";

interface UseRealtimeTopicOptions<T extends Event> {
    onMessage: TopicMessageHandler<T>;
    enabled?: boolean;
    initialRequestN?: number;
    autoSubscribe?: boolean;
}

/**
 * Subscribes to a realtime topic by topic-key. The hook resolves the concrete
 * topic-name and allowed-roles via REALTIME_TOPIC_ROLES, so entity hooks only
 * need to know the topic-key — the auth/transport plumbing is handled here.
 *
 * Tracking: every received message is automatically logged via
 * `trackReceivedWebsocketEvent`; entity-hook callers should not duplicate that
 * call inside their own onMessage.
 *
 * Cleanup: useRSocketTopic cancels the subscription when the component
 * unmounts, the topic-name changes, or the gating inputs (enabled, role,
 * connection) flip false. A separate fail-safe unmount effect guarantees
 * cancellation even if the subscription resolves after the component has
 * unmounted. Callers don't need their own unsubscribe effect.
 */
export function useRealtimeTopic<T extends Event>(topicKey: TopicKey, options: UseRealtimeTopicOptions<T>) {
    const { apiDomain } = useAuth();
    const topicName = apiDomain ? RSOCKET_TOPICS[topicKey](apiDomain) : null;
    const { onMessage } = options;

    return useRSocketTopic<T>({
        ...options,
        topicName,
        allowedRoles: REALTIME_TOPIC_ROLES[topicKey],
        onMessage: (message) => {
            if (topicName) {
                trackReceivedWebsocketEvent(message.type, topicName);
            }
            onMessage(message);
        },
    });
}
